import { COLOR_LABELS, type ColorPieEntry, type DeckAnalysis, type ManaCurveBucket, type MtgColor, type TypeBreakdown } from '@ugin/shared';
import { Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { useMemo } from 'react';
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { Badge } from '@/components/ui/badge';
import { useDeckAnalysis } from '@/hooks/useDecks';


const meta: WidgetMeta = {
  id: 'deck-visualizer',
  name: 'Shape',
  description: 'Mana curve, color identity, and types at a glance.',
  icon: Sparkles,
  defaultSize: { w: 6, h: 4 },
  category: 'deck',
};

const COLOR_HEX: Record<MtgColor, string> = {
  W: 'oklch(0.94 0.05 95)',
  U: 'oklch(0.65 0.17 250)',
  B: 'oklch(0.45 0.08 30)',
  R: 'oklch(0.65 0.22 25)',
  G: 'oklch(0.6 0.16 145)',
  C: 'oklch(0.78 0.02 80)',
};

export const DeckVisualizerWidget: WidgetComponent<{ deckId: string }> = ({ deckId }) => {
  const { data, isLoading, error, refetch } = useDeckAnalysis(deckId);
  const analysis = data?.analysis;

  return (
    <WidgetContainer
      meta={meta}
      icon={<Sparkles className="size-4" />}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      empty={!isLoading && !analysis}
      emptyTitle="Awaiting analysis"
      emptyDescription="Once cards are resolved Ugin will draw the curves."
      actions={
        // 'unknown' is the heuristic declining, not a finding about the deck.
        // Badging it reads as a verdict, so nothing is shown instead.
        analysis?.archetypeLabel && analysis.archetypeLabel !== 'unknown' ? (
          <Badge variant="accent" className="capitalize">
            {analysis.archetypeLabel}
          </Badge>
        ) : null
      }
    >
      {analysis ? <Charts analysis={analysis} /> : null}
    </WidgetContainer>
  );
};

DeckVisualizerWidget.meta = meta;

function Charts({ analysis }: { analysis: DeckAnalysis }) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      <ChartCard title="Mana curve">
        <ManaCurveChart curve={analysis.manaCurve} />
      </ChartCard>
      <ChartCard title="Color identity">
        <ColorPieChart pie={analysis.colorPie} />
      </ChartCard>
      <ChartCard title="Type breakdown">
        <TypeBreakdownChart breakdown={analysis.typeBreakdown} />
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-ugin-border/40 bg-ugin-bg-deep/30 p-3">
      <p className="mb-3 text-xs uppercase tracking-widest text-ugin-text-muted">{title}</p>
      <div className="h-44">{children}</div>
    </div>
  );
}

function ManaCurveChart({ curve }: { curve: ManaCurveBucket[] }) {
  const max = curve.reduce((m, b) => Math.max(m, b.count), 0);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={curve} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
        <XAxis
          dataKey="label"
          stroke="oklch(0.65 0.01 280)"
          fontSize={11}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          cursor={{ fill: 'oklch(0.27 0.02 280 / 0.5)' }}
          contentStyle={{
            background: 'oklch(0.22 0.015 280)',
            border: '1px solid oklch(0.32 0.025 280)',
            borderRadius: 8,
            fontSize: 12,
          }}
          labelStyle={{ color: 'oklch(0.92 0.005 280)' }}
          formatter={(value: number) => [`${value} cards`, '']}
          labelFormatter={(label: string) => `CMC ${label}`}
        />
        <Bar
          dataKey="count"
          fill="oklch(0.78 0.14 75)"
          radius={[4, 4, 0, 0]}
          isAnimationActive
          animationDuration={350}
        >
          {curve.map((b) => (
            <Cell
              key={b.cmc}
              fill={b.count === max && max > 0 ? 'oklch(0.85 0.16 75)' : 'oklch(0.65 0.1 75)'}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function ColorPieChart({ pie }: { pie: ColorPieEntry[] }) {
  const data = pie.length > 0 ? pie : [{ color: 'C' as MtgColor, count: 1, percentage: 100 }];
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip
            contentStyle={{
              background: 'oklch(0.22 0.015 280)',
              border: '1px solid oklch(0.32 0.025 280)',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(value: number, _name, entry) => {
              const color = (entry.payload as { color?: MtgColor } | undefined)?.color ?? 'C';
              return [`${value} pips (${COLOR_LABELS[color].name})`, ''];
            }}
          />
          <Pie
            data={data}
            dataKey="count"
            nameKey="color"
            innerRadius={28}
            outerRadius={62}
            paddingAngle={data.length > 1 ? 1 : 0}
            isAnimationActive
            animationDuration={350}
          >
            {data.map((entry) => (
              <Cell key={entry.color} fill={COLOR_HEX[entry.color]} stroke="oklch(0.18 0.01 280)" />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function TypeBreakdownChart({ breakdown }: { breakdown: TypeBreakdown }) {
  const data = useMemo(
    () => [
      { name: 'Creatures', count: breakdown.creatures, fill: 'oklch(0.6 0.16 145)' },
      { name: 'Spells', count: breakdown.spells, fill: 'oklch(0.65 0.17 250)' },
      { name: 'Lands', count: breakdown.lands, fill: 'oklch(0.78 0.02 80)' },
      { name: 'Artifacts', count: breakdown.artifacts, fill: 'oklch(0.6 0.04 80)' },
      { name: 'Enchantments', count: breakdown.enchantments, fill: 'oklch(0.94 0.05 95)' },
      { name: 'Planeswalkers', count: breakdown.planeswalkers, fill: 'oklch(0.6 0.22 295)' },
      { name: 'Other', count: breakdown.other, fill: 'oklch(0.5 0.01 280)' },
    ],
    [breakdown],
  );
  const filtered = data.filter((d) => d.count > 0);
  const total = filtered.reduce((s, d) => s + d.count, 0);
  return (
    <ul className="space-y-1.5 text-xs">
      {filtered.map((row) => (
        <li key={row.name} className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-ugin-text-muted">{row.name}</span>
            <span className="font-mono text-ugin-text">{row.count}</span>
          </div>
          <motion.div
            className="h-1.5 overflow-hidden rounded-full bg-ugin-bg-deep"
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: total > 0 ? `${(row.count / total) * 100}%` : '0%',
                background: row.fill,
              }}
            />
          </motion.div>
        </li>
      ))}
    </ul>
  );
}
